import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { Loader } from "src/components/Loader";

// Registry of the bundled legal documents. Each entry is a dynamic import so
// the (~1MB base64) payload becomes its own chunk that loads ONLY when a root
// super admin actually opens that document — it never touches the main bundle.
const DOC_LOADERS = {
  "software-license-agreement": {
    title: "Software License Agreement",
    load: () => import("./docs/softwareLicenseAgreement"),
  },
  mnda: {
    title: "Mutual Non-Disclosure Agreement",
    load: () => import("./docs/mnda"),
  },
};

// base64 (of UTF-8 bytes) -> string, without mangling multi-byte characters.
function decodeBase64Utf8(b64) {
  const binary = atob(b64.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder("utf-8").decode(bytes);
}

/**
 * Full-screen viewer for a bundled legal document.
 *
 * Access is restricted to the root super admin only. `isLoggedIn` gates the
 * session; `useIsRootSuperAdmin` gates the role. Anyone else is bounced to the
 * not-found page, so the route reveals nothing about the document's existence.
 * There is no static file to fetch — the content lives inside the guarded,
 * lazily-loaded chunk.
 */
export default function LegalDocViewer({ docKey, isLoggedIn }) {
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const doc = DOC_LOADERS[docKey];
  const allowed = isLoggedIn && isRootSuperAdmin && Boolean(doc);

  const [html, setHtml] = useState(null);

  useEffect(() => {
    let active = true;
    if (!allowed) return undefined;
    doc.load().then((mod) => {
      if (active) setHtml(decodeBase64Utf8(mod.default));
    });
    return () => {
      active = false;
    };
  }, [allowed, doc]);

  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/pagenotfound" replace />;
  if (html === null) return <Loader />;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, bgcolor: "#faf9f5" }}>
      <iframe
        title={doc.title}
        srcDoc={html}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </Box>
  );
}
